import { useEffect, useMemo, useRef } from "react";
import TagInput from "./TagInput";

function RecipeForm({formData, onChange, onSubmit, submitButtonText, onCancel, errorMsg, disabled = false}) {
    const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-400 focus:bg-white transition dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:bg-gray-700";
    const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5";
    const ingredientsRef = useRef(null);
    const instructionsRef = useRef(null);
    const rafIdRef = useRef(null);
    const imagePreviews = useMemo(() => {
        return (formData.images || [])
            .filter((file) => file instanceof File)
            .map((file, idx) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${idx}`,
                url: URL.createObjectURL(file),
            }));
    }, [formData.images]);

    const videoPreviews = useMemo(() => {
        return (formData.videos || [])
            .filter((file) => file instanceof File)
            .map((file, idx) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${idx}`,
                url: URL.createObjectURL(file),
            }));
    }, [formData.videos]);

    useEffect(() => {
        return () => {
            imagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
            videoPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
    }, [imagePreviews, videoPreviews]);

    const expandTextarea = (element) => {
        if (!element) return;
        element.style.height = "auto";
        element.style.height = `${element.scrollHeight}px`;
    };

    const collapseTextarea = (element) => {
        if (!element) return;
        element.style.height = "";
    };

    const handleAutoGrowChange = (e, textareaRef) => {
        onChange(e);
        if (document.activeElement === textareaRef.current) {
            rafIdRef.current = requestAnimationFrame(() => expandTextarea(textareaRef.current));
        }
    };

    const handleTextareaFocus = (textareaRef) => {
        expandTextarea(textareaRef.current);
    };

    const handleTextareaBlur = (textareaRef) => {
        collapseTextarea(textareaRef.current);
    };

    return (
        <div>
          {errorMsg && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 text-sm rounded-xl px-4 py-3 mb-5 text-center">
              {errorMsg}
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-5">

            <div>
              <label className={labelClass}>Title</label>
              <input
                name="title"
                type="text"
                value={formData.title}
                onChange={onChange}
                required
                placeholder="Give your recipe a name"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={onChange}
                rows="3"
                placeholder="What makes this recipe special?"
                className={inputClass + " resize-none"}
              />
            </div>

            <div>
              <label className={labelClass}>Ingredients</label>
              <textarea
                ref={ingredientsRef}
                name="ingredients"
                value={formData.ingredients}
                onChange={(e) => handleAutoGrowChange(e, ingredientsRef)}
                onFocus={() => handleTextareaFocus(ingredientsRef)}
                onBlur={() => handleTextareaBlur(ingredientsRef)}
                rows="5"
                placeholder="One ingredient per line&#10;e.g., 2 cups flour&#10;1 tsp salt"
                className={inputClass + " resize-none overflow-hidden"}
              />
            </div>

            <div>
              <label className={labelClass}>Instructions</label>
              <textarea
                ref={instructionsRef}
                name="instructions"
                value={formData.instructions}
                onChange={(e) => handleAutoGrowChange(e, instructionsRef)}
                onFocus={() => handleTextareaFocus(instructionsRef)}
                onBlur={() => handleTextareaBlur(instructionsRef)}
                rows="5"
                placeholder="One step per line&#10;e.g., Preheat oven to 350F&#10;Mix dry ingredients"
                className={inputClass + " resize-none overflow-hidden"}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={onChange}
                  className={inputClass}
                  placeholder="e.g., Italian, Asian"
                />
              </div>
              <div>
                <label className={labelClass}>Diet Preference</label>
                <input
                  type="text"
                  name="diet_preference"
                  value={formData.diet_preference}
                  onChange={onChange}
                  className={inputClass}
                  placeholder="e.g., Vegan, Keto"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Tags</label>
              <TagInput
                tags={formData.tag_names || []}
                onChange={(newTags) => onChange({ target: { name: "tag_names", value: newTags } })}
                placeholder="e.g., quick-meal, spicy, meal-prep"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Prep Time</label>
                <div className="relative">
                  <input
                    name="prep_time_value"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.prep_time_value ?? ''}
                    onChange={onChange}
                    placeholder="0"
                    className={inputClass + " pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"}
                  />
                  <select
                    name="prep_time_unit"
                    value={formData.prep_time_unit || "min"}
                    onChange={onChange}
                    className="absolute right-2 top-1/2 w-14 -translate-y-1/2 border-0 bg-transparent p-0 text-xs font-medium text-gray-400 focus:outline-none focus:ring-0 dark:text-gray-300"
                  >
                    <option value="min">min</option>
                    <option value="hr">hr</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Cook Time</label>
                <div className="relative">
                  <input
                    name="cook_time_value"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.cook_time_value ?? ''}
                    onChange={onChange}
                    placeholder="0"
                    className={inputClass + " pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"}
                  />
                  <select
                    name="cook_time_unit"
                    value={formData.cook_time_unit || "min"}
                    onChange={onChange}
                    className="absolute right-2 top-1/2 w-14 -translate-y-1/2 border-0 bg-transparent p-0 text-xs font-medium text-gray-400 focus:outline-none focus:ring-0 dark:text-gray-300"
                  >
                    <option value="min">min</option>
                    <option value="hr">hr</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Servings</label>
                <input
                  name="servings"
                  type="number"
                  value={formData.servings}
                  onChange={onChange}
                  placeholder="0"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Difficulty</label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={onChange}
                  className={inputClass}
                >
                  <option value="">Select</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Visibility</label>
                <select
                  name="visibility"
                  value={formData.visibility}
                  onChange={onChange}
                  className={inputClass}
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Photos</label>
              <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl py-6 cursor-pointer hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/30 transition">
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 dark:text-gray-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-400">
                    {formData.images?.length > 0
                      ? `${formData.images.length} photo${formData.images.length > 1 ? 's' : ''} selected`
                      : 'Click to upload photos'}
                  </span>
                </div>
                <input
                  name="images"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={onChange}
                  className="hidden"
                />
              </label>
              {formData.images?.length > 0 && (
                <div className="flex space-x-2 mt-3 overflow-x-auto">
                  {imagePreviews.map(({ key, url }, idx) => (
                    <img
                      key={key}
                      src={url}
                      alt={`Preview ${idx + 1}`}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={labelClass}>Video Clip (max 30s)</label>
              <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl py-6 cursor-pointer hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/30 transition">
                <div className="text-center">
                  <svg className="w-8 h-8 text-gray-300 dark:text-gray-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-400">
                    {formData.videos?.length > 0
                      ? `${formData.videos.length} video${formData.videos.length > 1 ? 's' : ''} selected`
                      : 'Click to upload a short clip'}
                  </span>
                </div>
                <input
                  name="videos"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={onChange}
                  className="hidden"
                />
              </label>
              {videoPreviews.length > 0 && (
                <div className="flex space-x-2 mt-3 overflow-x-auto">
                  {videoPreviews.map(({ key, url }) => (
                    <video
                      key={key}
                      src={url}
                      className="w-24 h-16 object-cover rounded-lg flex-shrink-0 bg-black"
                      muted
                      playsInline
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={disabled}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={disabled}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {submitButtonText}
              </button>
            </div>

          </form>
        </div>
    );
}

export default RecipeForm;
